import type { Supplier } from '../../types'

export type RiskBand = 'low' | 'medium' | 'high'

export type RollupRow = {
  key: string
  count: number
  high: number
  medium: number
  low: number
  unscored: number
  avg: number | null
}

/** Placeholders only — do not include bare "na" (North America region code). */
const PLACEHOLDER_KEYS = new Set([
  'not available',
  'not classified',
  'unavailable',
  'n/a',
  'tbd',
  '',
])

/** Low ≤4 | Medium ≤6 | High >6 (score-only). Matches product risk ratings. */
export function riskBand(value: number): RiskBand {
  if (value > 6) return 'high'
  if (value > 4) return 'medium'
  return 'low'
}

/** Coerce PostgREST NUMERIC strings so banding / collapse stay correct. */
export function numericScore(value: unknown): number | null {
  if (value == null || value === '') return null
  const n = typeof value === 'number' ? value : Number(value)
  return Number.isFinite(n) ? n : null
}

/**
 * Portfolio High/Med/Low from numeric overall_score only.
 * Gauge-only High labels (unscored Final Risk) are not High.
 */
export function supplierBand(s: Supplier): RiskBand | null {
  const score = numericScore(s.overall_score)
  if (score == null) return null
  return riskBand(score)
}

function isPlaceholderKey(raw: string): boolean {
  return PLACEHOLDER_KEYS.has(raw.trim().toLowerCase())
}

export type CollapsedSupplierRisk = {
  name: string
  category: string | null
  /** Display join of distinct locations (same as locations joined). */
  location: string | null
  /** Distinct non-empty locations across product lines for this name. */
  locations: string[]
  kenvue_region: string | null
  sub_category: string | null
  brands: string[]
  /** True if any product line is single-source; else from best row. */
  single_source: boolean | null
  /** Max overall_score across product lines; null when every line is unscored. */
  overall_score: number | null
  /** Product-line supplier row ids under this collapsed name. */
  productLineIds: string[]
  /** Representative product-line id (same row KPI region/category come from). */
  bestId: string
  key: string
}

export function collapsedBand(s: CollapsedSupplierRisk): RiskBand | null {
  const score = numericScore(s.overall_score)
  if (score == null) return null
  return riskBand(score)
}

/** Band KPIs on unique supplier names (max score). High + Med + Low + Unscored === total. */
export function bandCountsUnique(collapsed: CollapsedSupplierRisk[]): {
  total: number
  high: number
  medium: number
  low: number
  unscored: number
} {
  let high = 0
  let medium = 0
  let low = 0
  let unscored = 0
  for (const s of collapsed) {
    const b = collapsedBand(s)
    if (b === 'high') high++
    else if (b === 'medium') medium++
    else if (b === 'low') low++
    else unscored++
  }
  return { total: collapsed.length, high, medium, low, unscored }
}

/**
 * Unique-supplier band KPIs (collapse by name, max score).
 * Prefer this for Exec / IQ / Risk Analyst portfolio counts.
 */
export function bandCounts(suppliers: Supplier[]): {
  total: number
  high: number
  medium: number
  low: number
  unscored: number
} {
  return bandCountsUnique(collapseSuppliersByName(suppliers))
}

type RollupAcc = {
  total: number
  scores: number[]
  high: number
  medium: number
  low: number
  unscored: number
}

function emptyAcc(): RollupAcc {
  return { total: 0, scores: [], high: 0, medium: 0, low: 0, unscored: 0 }
}

function bumpAcc(entry: RollupAcc, band: RiskBand | null, score: number | null) {
  entry.total++
  if (score != null) entry.scores.push(score)
  if (band === 'high') entry.high++
  else if (band === 'medium') entry.medium++
  else if (band === 'low') entry.low++
  else entry.unscored++
}

function finalizeRollup(map: Map<string, RollupAcc>): RollupRow[] {
  return [...map.entries()]
    .map(([key, v]) => ({
      key,
      count: v.total,
      high: v.high,
      medium: v.medium,
      low: v.low,
      unscored: v.unscored,
      avg: v.scores.length ? v.scores.reduce((a, b) => a + b, 0) / v.scores.length : null,
    }))
    .sort((a, b) => b.high - a.high || b.count - a.count || a.key.localeCompare(b.key))
}

function normalizeRollupKey(raw: string | null | undefined): string {
  if (raw == null || !String(raw).trim() || isPlaceholderKey(String(raw))) return 'Unclassified'
  return String(raw).trim()
}

/**
 * Product-line dimension rollup (legacy). Prefer collapsed helpers for portfolio views.
 */
export function rollupSuppliers(
  items: Supplier[],
  keyFn: (s: Supplier) => string | null | undefined
): RollupRow[] {
  const map = new Map<string, RollupAcc>()

  for (const s of items) {
    const key = normalizeRollupKey(keyFn(s))
    const entry = map.get(key) ?? emptyAcc()
    bumpAcc(entry, supplierBand(s), numericScore(s.overall_score))
    map.set(key, entry)
  }

  return finalizeRollup(map)
}

/** One unique supplier per dimension value (from representative / best product line). */
export function rollupCollapsedByAttribute(
  collapsed: CollapsedSupplierRisk[],
  keyFn: (s: CollapsedSupplierRisk) => string | null | undefined
): RollupRow[] {
  const map = new Map<string, RollupAcc>()

  for (const s of collapsed) {
    const key = normalizeRollupKey(keyFn(s))
    const entry = map.get(key) ?? emptyAcc()
    bumpAcc(entry, collapsedBand(s), s.overall_score)
    map.set(key, entry)
  }

  return finalizeRollup(map)
}

/**
 * Brand-impact rollup: each unique supplier increments once per brand they serve.
 * High-risk suppliers with 9 brand rows contribute 9 to High across brand keys.
 */
export function rollupCollapsedByBrand(collapsed: CollapsedSupplierRisk[]): RollupRow[] {
  const map = new Map<string, RollupAcc>()

  for (const s of collapsed) {
    const brandKeys =
      s.brands.length > 0 ? s.brands.map((b) => normalizeRollupKey(b)) : ['Unclassified']
    const band = collapsedBand(s)
    for (const key of brandKeys) {
      const entry = map.get(key) ?? emptyAcc()
      bumpAcc(entry, band, s.overall_score)
      map.set(key, entry)
    }
  }

  return finalizeRollup(map)
}

/**
 * Country-impact rollup: each unique supplier increments once per location they serve.
 */
export function rollupCollapsedByLocation(collapsed: CollapsedSupplierRisk[]): RollupRow[] {
  const map = new Map<string, RollupAcc>()

  for (const s of collapsed) {
    const locs =
      s.locations.length > 0
        ? s.locations
        : s.location
          ? [s.location]
          : []
    const locationKeys =
      locs.length > 0 ? locs.map((l) => normalizeRollupKey(l)) : ['Unclassified']
    const band = collapsedBand(s)
    for (const key of locationKeys) {
      const entry = map.get(key) ?? emptyAcc()
      bumpAcc(entry, band, s.overall_score)
      map.set(key, entry)
    }
  }

  return finalizeRollup(map)
}

export function categoryKey(s: Supplier): string | null {
  return s.category || s.industry || null
}

export function subCategoryKey(s: Supplier): string | null {
  return s.sub_category || null
}

export function locationKey(s: Supplier): string | null {
  return s.location || null
}

export function brandKey(s: Supplier): string | null {
  return s.brand || null
}

export function collapsedCategoryKey(s: CollapsedSupplierRisk): string | null {
  return s.category
}

export function collapsedSubCategoryKey(s: CollapsedSupplierRisk): string | null {
  return s.sub_category
}

export function collapsedLocationKey(s: CollapsedSupplierRisk): string | null {
  if (s.locations.length === 1) return s.locations[0]
  if (s.locations.length > 1) return s.locations.join(', ')
  return s.location
}

export function collapsedRegionKey(s: CollapsedSupplierRisk): string | null {
  return s.kenvue_region
}

const BRAND_DISPLAY_LIMIT = 3

function richness(s: Supplier): number {
  return [s.category || s.industry, s.location, s.brand].filter((v) => v && String(v).trim()).length
}

/** Unique brands joined with commas (for copy / full list). */
export function formatBrandsFull(brands: string[]): string {
  return brands.join(', ')
}

/**
 * On-screen brand list: up to 3 brands, then ` · +N more` when truncated.
 */
export function formatBrandsDisplay(brands: string[], limit = BRAND_DISPLAY_LIMIT): string {
  if (brands.length === 0) return ''
  if (brands.length <= limit) return brands.join(', ')
  const shown = brands.slice(0, limit).join(', ')
  return `${shown} · +${brands.length - limit} more`
}

/** Unique locations joined with commas. */
export function formatLocationsFull(locations: string[]): string {
  return locations.join(', ')
}

/**
 * Portfolio list/profile title.
 * Prefer collapse `display_name`; for raw Indirect rows use `parent_name`; else legal `name`.
 */
export function supplierDisplayName(
  s: Pick<Supplier, 'name' | 'display_name' | 'parent_name' | 'procurement_type'>
): string {
  const display = (s.display_name || '').trim()
  if (display) return display
  const parent = (s.parent_name || '').trim()
  if (parent && s.procurement_type === 'indirect') return parent
  return (s.name || '').trim()
}

/**
 * All product-line ids in the same portfolio-collapsed group as `id`
 * (Indirect parent group or Direct legal-name group). Used for Dashboard filters.
 */
export function supplierGroupIds(
  id: string,
  scopedSuppliers: Supplier[],
  procurementType: 'all' | 'direct' | 'indirect' = 'all'
): string[] {
  const raw = scopedSuppliers.find((s) => s.id === id)
  if (!raw) return id ? [id] : []

  const collapsed = collapseToBestSupplierRowsForPortfolio(scopedSuppliers, procurementType)
  const group =
    collapsed.find((s) => s.id === id) ||
    collapsed.find((s) => {
      const lines = s.product_line_names || []
      const rawName = (raw.name || '').trim().toLowerCase()
      if (rawName && lines.some((n) => n.trim().toLowerCase() === rawName)) return true
      const rawParent = (raw.parent_name || '').trim().toLowerCase()
      const display = (s.display_name || s.name || '').trim().toLowerCase()
      return !!rawParent && rawParent === display
    })

  if (!group) return [id]

  const displayKey = (group.display_name || group.name || '').trim().toLowerCase()
  const lineKeys = new Set(
    (group.product_line_names || []).map((n) => n.trim().toLowerCase()).filter(Boolean)
  )

  const ids = scopedSuppliers
    .filter((s) => {
      if (s.id === group.id) return true
      const nameKey = (s.name || '').trim().toLowerCase()
      if (nameKey && lineKeys.has(nameKey)) return true
      const parentKey = (s.parent_name || '').trim().toLowerCase()
      if (displayKey && parentKey === displayKey) return true
      return false
    })
    .map((s) => s.id)

  return ids.length ? [...new Set(ids)] : [id]
}

function rowMatchesCollapsedGroup(row: Supplier, supplier: Supplier): boolean {
  const parentKey = (supplier.parent_name || supplier.display_name || '').trim().toLowerCase()
  if (parentKey) {
    const rowParent = (row.parent_name || '').trim().toLowerCase()
    if (rowParent && rowParent === parentKey) return true
  }
  const lineNames = (supplier.product_line_names || [])
    .map((n) => n.trim().toLowerCase())
    .filter(Boolean)
  const rowName = (row.name || '').trim().toLowerCase()
  if (rowName && lineNames.includes(rowName)) return true
  const nameKey = (supplier.name || '').trim().toLowerCase()
  if (nameKey && rowName === nameKey) return true
  const displayKey = (supplier.display_name || '').trim().toLowerCase()
  if (displayKey && rowName === displayKey) return true
  return false
}

/**
 * Merge brand + location display fields from sibling product lines.
 * Matches by parent key / product_line_names when present (Indirect), else exact name.
 */
export function enrichSupplierDisplayFromSiblings(
  supplier: Supplier,
  siblings: Supplier[]
): Supplier {
  const brandSet = new Map<string, string>()
  const locationSet = new Map<string, string>()
  let matched = 0
  for (const row of siblings) {
    if (!rowMatchesCollapsedGroup(row, supplier)) continue
    matched += 1
    const brand = (row.brand || '').trim()
    if (brand && !isPlaceholderKey(brand) && !brand.includes(',')) {
      const bk = brand.toLowerCase()
      if (!brandSet.has(bk)) brandSet.set(bk, brand)
    } else if (brand && brand.includes(',')) {
      for (const part of brand.split(',')) {
        const b = part.trim()
        if (!b || isPlaceholderKey(b)) continue
        const bk = b.toLowerCase()
        if (!brandSet.has(bk)) brandSet.set(bk, b)
      }
    }
    const loc = (row.location || '').trim()
    if (loc && !isPlaceholderKey(loc) && !loc.includes(',')) {
      const lk = loc.toLowerCase()
      if (!locationSet.has(lk)) locationSet.set(lk, loc)
    } else if (loc && loc.includes(',')) {
      for (const part of loc.split(',')) {
        const l = part.trim()
        if (!l || isPlaceholderKey(l)) continue
        const lk = l.toLowerCase()
        if (!locationSet.has(lk)) locationSet.set(lk, l)
      }
    }
  }
  if (matched === 0) return supplier
  const brands = [...brandSet.values()].sort((a, b) => a.localeCompare(b))
  const locations = [...locationSet.values()].sort((a, b) => a.localeCompare(b))
  return {
    ...supplier,
    brand: brands.length ? formatBrandsFull(brands) : supplier.brand,
    location: locations.length ? formatLocationsFull(locations) : supplier.location,
  }
}

/**
 * Resolve a product-line id to the portfolio-collapsed row for profile open / deep-link.
 * Never returns a raw child without re-applying collapse display fields.
 */
export function resolveCollapsedProfileSupplier(
  id: string,
  base: Supplier[],
  scopedSuppliers: Supplier[],
  procurementType: 'all' | 'direct' | 'indirect'
): Supplier | null {
  const fromBase = base.find((s) => s.id === id)
  if (fromBase) return enrichSupplierDisplayFromSiblings(fromBase, scopedSuppliers)

  const raw = scopedSuppliers.find((s) => s.id === id)
  if (!raw) return null

  const collapsed = collapseToBestSupplierRowsForPortfolio(scopedSuppliers, procurementType)
  const byId = collapsed.find((s) => s.id === id)
  if (byId) return enrichSupplierDisplayFromSiblings(byId, scopedSuppliers)

  // Child id may not be the group's "best" representative — map via parent/legal key.
  const rawKey = indirectParentKey(raw) || {
    key: (raw.name || '').trim().toLowerCase(),
    displayName: (raw.name || '').trim(),
  }
  if (!rawKey.key) return enrichSupplierDisplayFromSiblings(raw, scopedSuppliers)
  const byGroup = collapsed.find((s) => {
    const display = (s.display_name || s.name || '').trim().toLowerCase()
    if (display === rawKey.key) return true
    const lines = s.product_line_names || []
    return lines.some((n) => n.trim().toLowerCase() === (raw.name || '').trim().toLowerCase())
  })
  if (byGroup) return enrichSupplierDisplayFromSiblings(byGroup, scopedSuppliers)
  return enrichSupplierDisplayFromSiblings(raw, scopedSuppliers)
}

/** True when supplier location field (possibly comma-joined) matches a country filter. */
export function supplierMatchesLocation(
  s: { location?: string | null },
  countryFilter: string
): boolean {
  if (!countryFilter) return true
  const q = countryFilter.toLowerCase()
  const loc = (s.location || '').trim()
  if (!loc) return false
  return loc
    .split(',')
    .map((p) => p.trim().toLowerCase())
    .some((p) => p === q)
}

export function collapsedMatchesLocation(
  s: Pick<CollapsedSupplierRisk, 'location' | 'locations'>,
  countryFilter: string
): boolean {
  if (!countryFilter) return true
  const q = countryFilter.toLowerCase()
  const locs =
    s.locations.length > 0
      ? s.locations
      : s.location
        ? s.location.split(',').map((p) => p.trim()).filter(Boolean)
        : []
  return locs.some((l) => l.toLowerCase() === q)
}

function indirectParentKey(
  s: Supplier
): { key: string; displayName: string } | null {
  const parent = (s.parent_name || '').trim()
  if (parent) return { key: parent.toLowerCase(), displayName: parent }
  const name = (s.name || '').trim()
  if (!name) return null
  return { key: name.toLowerCase(), displayName: name }
}

function addUniqueLabel(map: Map<string, string>, raw: string | null | undefined) {
  const value = (raw || '').trim()
  if (!value || isPlaceholderKey(value)) return
  // Already-joined display strings are split so siblings don't create bogus keys.
  for (const part of value.split(',')) {
    const label = part.trim()
    if (!label || isPlaceholderKey(label)) continue
    const key = label.toLowerCase()
    if (!map.has(key)) map.set(key, label)
  }
}

/**
 * Materialize Intelligence View table rows from the same collapse grain as KPIs
 * so region / score / band filters never diverge.
 */
function materializeListRowsFromCollapsed(
  suppliers: Supplier[],
  collapsed: CollapsedSupplierRisk[]
): Supplier[] {
  const byId = new Map(suppliers.map((s) => [s.id, s]))
  const rows: Supplier[] = []
  for (const c of collapsed) {
    const best =
      (c.bestId ? byId.get(c.bestId) : undefined) ||
      c.productLineIds.map((id) => byId.get(id)).find((s): s is Supplier => !!s)
    if (!best) continue
    const legalNames = new Map<string, string>()
    for (const id of c.productLineIds) {
      const row = byId.get(id)
      if (row) addUniqueLabel(legalNames, row.name)
    }
    const productLineNames = [...legalNames.values()].sort((a, b) => a.localeCompare(b))
    rows.push({
      ...best,
      name: c.name,
      display_name: c.name,
      product_line_names: productLineNames,
      overall_score: c.overall_score,
      brand: c.brands.length ? c.brands.join(', ') : best.brand,
      location: c.location ?? best.location,
      kenvue_region: c.kenvue_region,
      category: c.category ?? best.category,
      sub_category: c.sub_category ?? best.sub_category,
      single_source: c.single_source,
    })
  }
  return rows.sort(
    (a, b) =>
      (numericScore(b.overall_score) ?? -1) - (numericScore(a.overall_score) ?? -1) ||
      supplierDisplayName(a).localeCompare(supplierDisplayName(b))
  )
}

/**
 * One row per unique supplier name for list tables.
 * Same grain as collapseSuppliersByName (KPI collapse).
 */
export function collapseToBestSupplierRows(suppliers: Supplier[]): Supplier[] {
  return materializeListRowsFromCollapsed(suppliers, collapseSuppliersByName(suppliers))
}

/**
 * One row per unique Indirect parent (fallback to legal name if parent missing).
 * Same grain as collapseSuppliersByParentName (KPI collapse).
 */
export function collapseToBestSupplierRowsByParent(suppliers: Supplier[]): Supplier[] {
  return materializeListRowsFromCollapsed(suppliers, collapseSuppliersByParentName(suppliers))
}

/**
 * Portfolio-aware list grain: Direct by legal name; Indirect by parent name.
 * Materialized from collapseSuppliersForPortfolio so table matches KPIs.
 */
export function collapseToBestSupplierRowsForPortfolio(
  list: Supplier[],
  procurementType: 'all' | 'direct' | 'indirect'
): Supplier[] {
  return materializeListRowsFromCollapsed(
    list,
    collapseSuppliersForPortfolio(list, procurementType)
  )
}

/**
 * One row per unique supplier name (case-insensitive).
 * Score = max overall_score (null if every product line is unscored);
 * brands = unique sorted non-empty brands across all lines.
 */
export function collapseSuppliersByName(suppliers: Supplier[]): CollapsedSupplierRisk[] {
  return collapseSuppliersByKey(suppliers, (s) => {
    const name = (s.name || '').trim()
    if (!name) return null
    return { key: name.toLowerCase(), displayName: name }
  })
}

/**
 * One row per unique Indirect parent name (fallback to legal name if parent missing).
 * Display `name` on the collapsed row is the parent string.
 */
export function collapseSuppliersByParentName(suppliers: Supplier[]): CollapsedSupplierRisk[] {
  return collapseSuppliersByKey(suppliers, indirectParentKey)
}

function collapseSuppliersByKey(
  suppliers: Supplier[],
  resolveKey: (s: Supplier) => { key: string; displayName: string } | null
): CollapsedSupplierRisk[] {
  const groups = new Map<
    string,
    {
      displayName: string
      rows: Supplier[]
      maxScore: number | null
      best: Supplier
    }
  >()

  for (const s of suppliers) {
    const resolved = resolveKey(s)
    if (!resolved) continue
    const score = numericScore(s.overall_score)
    const existing = groups.get(resolved.key)

    if (!existing) {
      groups.set(resolved.key, {
        displayName: resolved.displayName,
        rows: [s],
        maxScore: score,
        best: s,
      })
      continue
    }

    existing.rows.push(s)
    if (score == null) continue

    if (
      existing.maxScore == null ||
      score > existing.maxScore ||
      (score === existing.maxScore && richness(s) > richness(existing.best))
    ) {
      existing.maxScore = score
      existing.best = s
      existing.displayName = resolved.displayName
    }
  }

  return [...groups.entries()]
    .map(([key, g]) => {
      const brandSet = new Map<string, string>()
      const locationSet = new Map<string, string>()
      for (const row of g.rows) {
        addUniqueLabel(brandSet, row.brand)
        addUniqueLabel(locationSet, row.location)
      }
      const brands = [...brandSet.values()].sort((a, b) => a.localeCompare(b))
      const locations = [...locationSet.values()].sort((a, b) => a.localeCompare(b))
      let single_source: boolean | null = null
      if (g.rows.some((r) => r.single_source === true)) single_source = true
      else if (g.best.single_source === false) single_source = false
      else if (g.rows.some((r) => r.single_source === false)) single_source = false
      const productLineIds = g.rows.map((r) => r.id).filter(Boolean)
      return {
        name: g.displayName,
        category: g.best.category || g.best.industry || null,
        location: locations.length ? locations.join(', ') : g.best.location || null,
        locations,
        kenvue_region: g.best.kenvue_region || null,
        sub_category: g.best.sub_category || null,
        brands,
        single_source,
        overall_score: g.maxScore,
        productLineIds,
        bestId: g.best.id || productLineIds[0] || '',
        key,
      }
    })
    .sort((a, b) => {
      const as = a.overall_score
      const bs = b.overall_score
      if (as == null && bs == null) return a.name.localeCompare(b.name)
      if (as == null) return 1
      if (bs == null) return -1
      return bs - as || a.name.localeCompare(b.name)
    })
}

export type CollapsedSupplierRiskWithProcurement = CollapsedSupplierRisk & {
  procurement_type: 'direct' | 'indirect'
}

/**
 * Unique suppliers per Direct/Indirect tag.
 * Direct: by legal name. Indirect: by parent name.
 * Null/legacy procurement_type is treated as Direct.
 */
export function collapseSuppliersByNameAndProcurement(
  list: Supplier[]
): CollapsedSupplierRiskWithProcurement[] {
  const direct = collapseSuppliersByName(
    list.filter((s) => s.procurement_type !== 'indirect')
  ).map((s) => ({ ...s, procurement_type: 'direct' as const }))
  const indirect = collapseSuppliersByParentName(
    list.filter((s) => s.procurement_type === 'indirect')
  ).map((s) => ({ ...s, procurement_type: 'indirect' as const }))
  return [...direct, ...indirect]
}

/**
 * Portfolio-aware unique-supplier collapse.
 * Direct: by name. Indirect: by parent. All: both separately (no cross-merge).
 */
export function collapseSuppliersForPortfolio(
  list: Supplier[],
  procurementType: 'all' | 'direct' | 'indirect'
): CollapsedSupplierRisk[] {
  if (procurementType === 'all') return collapseSuppliersByNameAndProcurement(list)
  if (procurementType === 'indirect') return collapseSuppliersByParentName(list)
  return collapseSuppliersByName(list)
}
